import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { downloadGraphFileApi } from "@/api/api"
import { FileText, Upload, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import { FilePickerDialog } from "@/features/chat/components/file-picker-dialog"
import { graphDownloadErrorMessage } from "@/features/files/graph-download-error-message"
import { logger } from "@/lib/logger"
import { SettingsService } from "@/services/settings-service"

type PromptFileSettingsProps = {
  files: { filename: string; uploaded_at?: string }[]
  onFilesAdd: (files: File[]) => void
  onFileRemove: (filename: string) => void
  isUploading?: boolean
  isDeleting?: boolean
}

export function PromptFileSettings({
  files,
  onFilesAdd,
  onFileRemove,
  isUploading,
  isDeleting,
}: PromptFileSettingsProps) {
  const { t } = useTranslation("", { keyPrefix: "prompt_settings.files" })
  const [filePickerOpen, setFilePickerOpen] = useState(false)
  const [settingsService] = useState<SettingsService>(SettingsService.getInstance())

  return (
    <>
      <div className="space-y-6">
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium">{t("upload_files")}</h4>
            <p className="text-muted-foreground mt-1 text-xs">{t("upload_files_description")}</p>
            <p className="text-muted-foreground mt-2 text-xs italic">{t("file_picker_note")}</p>
          </div>

          <div className="space-y-3">
            <Button
              className="gap-2"
              disabled={isUploading}
              variant="outline"
              onClick={() => setFilePickerOpen(true)}
            >
              <Upload className="h-4 w-4" />
              {t("choose_files")}
              {isUploading && <Spinner className="h-3 w-3" />}
            </Button>
            <p className="text-muted-foreground text-xs">{t("supported_formats")}</p>
          </div>
        </div>

        <Separator />

        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium">{t("template_files")}</h4>
            <p className="text-muted-foreground mt-1 text-xs">
              {files.length} {t("files_uploaded")}
            </p>
          </div>

          {files.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <FileText className="text-muted-foreground/50 mb-2 h-8 w-8" />
              <p className="text-muted-foreground text-sm">{t("no_files")}</p>
            </div>
          )}

          {files.length > 0 && (
            <div className="space-y-1">
              {files.map((file) => (
                <div
                  key={file.filename}
                  className="hover:bg-accent/50 flex items-center justify-between rounded-lg border px-3 py-2 transition-colors"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                    <span className="truncate text-sm" title={file.filename}>
                      {file.filename}
                    </span>
                  </div>
                  <Button
                    className="hover:bg-destructive hover:text-destructive-foreground h-7 w-7 p-0"
                    disabled={isDeleting}
                    size="sm"
                    variant="ghost"
                    onClick={() => onFileRemove(file.filename)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <FilePickerDialog
        open={filePickerOpen}
        source="recent"
        onOpenChange={setFilePickerOpen}
        onLocalFilesSelected={(selected) => {
          onFilesAdd(selected)
        }}
        onSelect={async (item) => {
          try {
            // Download the Graph file and convert to File object
            const blob = await downloadGraphFileApi(item, settingsService.token)
            const mime = item.mimeType || blob.type || "application/octet-stream"
            const file = new File([blob], item.name, { type: mime })
            onFilesAdd([file])
          } catch (error) {
            logger.error(
              "Graph file download failed",
              error instanceof Error ? error : new Error(String(error))
            )
            toast.error(graphDownloadErrorMessage(error))
          }
        }}
      />
    </>
  )
}
